// visual-qa-trigger: issue #203 — validação pós-merge PRs #198/#191/#190 (staging v3)
import { useState, useEffect, useMemo, useCallback, useRef, type ComponentType } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'
import toast from 'react-hot-toast'

import {
  ArrowUp,
  ArrowDown,
  BadgeCheck,
  CalendarDays,
  Check,
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Search,
  UserCheck,
  UserCircle,
  Wrench,
  X,
  XCircle,
  Timer,
  Mail,
  FileCheck,
  Archive,
  Download,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useMaintenance } from '../hooks/useMaintenance'
import { usePartners } from '../hooks/usePartners'
import { useStoresWithUsage } from '../hooks/useStoresWithUsage'
import { MaintenanceItem, MaintenanceFilters, MaintenanceRequest, MaintenanceRegistration, MaintenanceStatus, LegacyStatus } from '../types/maintenance'
import { apiService } from '../services/api'
import { MultiSelectCheckbox, Header, LastModifiedIndicator } from '../components/shared'
import { getSystemVersion } from '../utils/version'
import { getStatusLabel, expandStatusFilter, STATUS_FILTER_TOKEN_APROVADO_MANUTENCAO, STATUS_FILTER_TOKEN_APROVADO_COMPRAS } from '../utils/status'
import OptimizedTable from '../components/shared/OptimizedTable'
import { TransferResponsibilityModal, ApprovalModal, RejectionModal, MarkExecutedModal, TriageModal, SendQuotationsModal, CFOValueConfirmModal, ApproveQuotationModal, ExportReportModal, DescriptionPreview } from '../components/maintenance'
import { truncateDescriptionPreview } from '../utils/descriptionPreview'
import type { Column } from '../components/shared/Table'
import type { NavigationContextPayload } from './MaintenanceForm'

const STATUS_OPTIONS = [
  { label: 'Aguardando Triagem', value: 'AGUARDANDO_TRIAGEM' },
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Pendente do Responsável', value: STATUS_FILTER_TOKEN_APROVADO_MANUTENCAO },
  { label: 'Aprovado', value: STATUS_FILTER_TOKEN_APROVADO_COMPRAS },
  { label: 'Executado', value: 'EXECUTADO' },
  { label: 'Rejeitado', value: 'REJEITADO' },
  { label: 'Concluído', value: 'CONCLUIDO' },
  { label: 'Arquivado', value: 'ARQUIVADO' },
]

const URGENCY_OPTIONS = [
  { label: 'Baixa', value: 'baixa' },
  { label: 'Média', value: 'media' },
  { label: 'Alta', value: 'alta' },
  { label: 'Crítica', value: 'critica' },
]

const SUBCATEGORY_OPTIONS = [
  { label: 'Manutenção', value: 'manutencao' },
  { label: 'Compras', value: 'compras' },
]

const SORT_OPTIONS = [
  { value: 'requestDate', label: 'Data de Criação' },
  { value: 'lastModified', label: 'Última Modificação' },
  { value: 'equipmentId', label: 'ID do Equipamento' },
  { value: 'title', label: 'Título' }
]

const SORT_VALUES = SORT_OPTIONS.map(option => option.value)

const SORT_ORDER_VALUES = ['asc', 'desc']

const FILTER_ARRAY_FIELDS = ['status', 'urgency', 'subcategory', 'department', 'responsibleId'] as const

// `location.state` é totalmente user-controlled (devtools, history.replaceState,
// link crafted). Nunca confiar nele sem validar em runtime — estados maliciosos
// como `page: -1`, `sortBy: "<script>"` ou `filters: { status: "not-array" }`
// são descartados silenciosamente.
const isValidNavigationState = (state: unknown): state is MaintenanceListNavigationStateFlat => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return false
  }
  const candidate = state as Record<string, unknown>

  if (candidate.filters !== undefined) {
    if (!candidate.filters || typeof candidate.filters !== 'object' || Array.isArray(candidate.filters)) {
      return false
    }
    const filters = candidate.filters as Record<string, unknown>
    for (const field of FILTER_ARRAY_FIELDS) {
      if (filters[field] !== undefined && !Array.isArray(filters[field])) {
        return false
      }
    }
    if (filters.sortBy !== undefined && typeof filters.sortBy !== 'string') {
      return false
    }
    if (filters.sortOrder !== undefined && !SORT_ORDER_VALUES.includes(filters.sortOrder as string)) {
      return false
    }
  }

  if (candidate.page !== undefined) {
    if (typeof candidate.page !== 'number' || !Number.isFinite(candidate.page) || candidate.page < 1) {
      return false
    }
  }

  if (candidate.sortBy !== undefined) {
    if (typeof candidate.sortBy !== 'string' || !SORT_VALUES.includes(candidate.sortBy)) {
      return false
    }
  }

  if (candidate.sortOrder !== undefined && !SORT_ORDER_VALUES.includes(candidate.sortOrder as string)) {
    return false
  }

  return true
}

/**
 * Chips de filtro rápido por localização são derivados das lojas que
 * aparecem em ≥1 ativo (endpoint `GET /api/stores/with-usage`), em vez
 * do array hardcoded `LOCATION_OPTIONS` da PR #215. A fonte da verdade
 * é a planilha "Base de Dados" (coluna B = Loja) — abreviações devem
 * ser feitas directamente na planilha antes do deploy.
 */

const URGENCY_ACCENT_CLASSES: Record<MaintenanceRequest['urgency'], string> = {
  baixa: 'bg-brand-neutral-300',
  media: 'bg-brand-warning-500',
  alta: 'bg-brand-accent-500',
  critica: 'bg-red-500',
}

const STATUS_BADGE_MAP: Record<MaintenanceStatus | LegacyStatus, string> = {
  AGUARDANDO_TRIAGEM: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200',
  PENDENTE: 'bg-brand-warning-50 text-brand-warning-700 ring-1 ring-inset ring-brand-warning-200',
  APROVADO: 'bg-brand-primary-50 text-brand-primary-700 ring-1 ring-inset ring-brand-primary-200',
  EXECUTADO: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  REJEITADO: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  CONCLUIDO: 'bg-brand-accent-50 text-brand-accent-700 ring-1 ring-inset ring-brand-accent-200',
  ARQUIVADO: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200',
  // Legado para compatibilidade
  concluido: 'bg-brand-accent-50 text-brand-accent-700 ring-1 ring-inset ring-brand-accent-200',
  // Novos status em lowercase (compatibilidade com backend)
  aprovado: 'bg-brand-primary-50 text-brand-primary-700 ring-1 ring-inset ring-brand-primary-200',
  pendente: 'bg-brand-warning-50 text-brand-warning-700 ring-1 ring-inset ring-brand-warning-200',
  rejeitado: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
}

const ACTION_BUTTON_STYLES = {
  view: 'border-brand-neutral-300 text-brand-text-secondary hover:bg-brand-surface-100 focus-visible:ring-brand-neutral-400',
  approve: 'border-brand-neutral-300 text-brand-primary-600 hover:bg-brand-primary-50 focus-visible:ring-brand-neutral-400',
  reject: 'border-brand-neutral-300 text-red-600 hover:bg-red-50 focus-visible:ring-brand-neutral-400',
  complete: 'border-brand-neutral-300 text-brand-accent-600 hover:bg-brand-warning-50 focus-visible:ring-brand-neutral-400',
}


const STATUS_ICONS: Record<MaintenanceStatus | LegacyStatus, ComponentType<{ className?: string }>> = {
  AGUARDANDO_TRIAGEM: FileCheck,
  PENDENTE: Clock3,
  APROVADO: CheckCircle2,
  EXECUTADO: Wrench,
  REJEITADO: XCircle,
  CONCLUIDO: BadgeCheck,
  ARQUIVADO: Archive,
  // Legado para compatibilidade
  concluido: BadgeCheck,
  // Novos status em lowercase (compatibilidade com backend)
  aprovado: CheckCircle2,
  pendente: Clock3,
  rejeitado: XCircle,
}

const getUrgencyAccentClass = (item: MaintenanceItem) =>
  isMaintenanceRequest(item) ? URGENCY_ACCENT_CLASSES[item.urgency] : 'bg-brand-neutral-300'

const getUrgencyTooltip = (item: MaintenanceItem) =>
  isMaintenanceRequest(item) ? `Urgência: ${capitalize(item.urgency)}` : 'Registro concluído'

type ActionButtonVariant = keyof typeof ACTION_BUTTON_STYLES

interface ActionIconButtonProps {
  title: string
  variant: ActionButtonVariant
  icon: ComponentType<{ className?: string }>
  onClick: () => void
  disabled?: boolean
  testId?: string
}

const ActionIconButton = ({
  title,
  variant,
  icon: Icon,
  onClick,
  disabled = false,
  testId,
}: ActionIconButtonProps) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    data-testid={testId}
    onClick={(event) => {
      event.stopPropagation()
      onClick()
    }}
    className={clsx(
      'inline-flex h-9 w-9 items-center justify-center rounded-full border text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60',
      ACTION_BUTTON_STYLES[variant],
    )}
  >
    <Icon className="h-4 w-4" aria-hidden />
    <span className="sr-only">{title}</span>
  </button>
)

const isMaintenanceRequest = (item: MaintenanceItem): item is MaintenanceRequest => item.type === 'requisicao'

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

// Normaliza o nome da loja para um test-id HTML seguro e estável:
// remove acentos, troca caracteres não-alfanuméricos por `-` e mete em
// lowercase. Usado pelo data-testid do chip (`location-chip-<slug>`).
const slugifyStore = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'store'
}

// Conjunto máximo de "irmãos" resolvido a partir do conjunto filtrado completo.
// As setas prev/next navegam dentro deste conjunto — não apenas dentro da
// página actual (issue #208).
const SIBLING_FETCH_LIMIT = 500

// Shape partilhado para `location.state` ao navegar da listagem para o detalhe.
// Unificado com a PR #213 (factory/issue-206):
//  - `listRestore`: o que a listagem precisa para restaurar filtros/ordenação/
//    paginação ao voltar ("Voltar" da #213).
//  - `navigationContext`: o que o formulário precisa para as setas prev/next
//    dentro do filtro activo (issue #208).
//
// A PR #213 (factory/issue-206) usa um formato FLAT em `location.state`
// ({ filters, page, sortBy, sortOrder } no top-level). Para coexistir com ela,
// o `handleView` escreve AMBOS os formatos (nested + flat) e o mount lê os dois
// via `normalizeNavigationState` (adapter backwards-compat) — independentemente
// de qual PR merge primeiro, o restore de filtros e as setas continuam a
// funcionar.
export interface MaintenanceListNavigationState {
  listRestore: {
    filters: MaintenanceFilters
    page: number
    sortBy: string
    sortOrder: 'asc' | 'desc'
  }
  navigationContext?: NavigationContextPayload
}

// Formato flat da PR #213 — as mesmas 4 chaves no top-level do `location.state`.
interface MaintenanceListNavigationStateFlat {
  filters: MaintenanceFilters
  page: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

// `location.state` é totalmente user-controlled (devtools, history.replaceState,
// link crafted). Normaliza o estado de navegação para `{ listRestore, navigationContext }`
// aceitando tanto o formato nested desta PR ({ listRestore, navigationContext })
// quanto o formato flat da PR #213 ({ filters, page, sortBy, sortOrder } no topo).
// Estados maliciosos/malformados são descartados silenciosamente.
const normalizeNavigationState = (
  rawState: unknown,
): { listRestore: MaintenanceListNavigationState['listRestore'] | null; navigationContext?: NavigationContextPayload } => {
  if (!isPlainRecord(rawState)) {
    return { listRestore: null }
  }
  if ('listRestore' in rawState) {
    const listRestore = rawState.listRestore as MaintenanceListNavigationState['listRestore'] | undefined
    const navigationContext = rawState.navigationContext as NavigationContextPayload | undefined
    return {
      listRestore: isPlainRecord(listRestore) && isPlainRecord(listRestore.filters)
        ? listRestore
        : null,
      navigationContext,
    }
  }
  // Formato flat da PR #213 — valida estritamente em runtime (mesmo guard
  // do #213) antes de reconstruir o `listRestore` para o mount.
  if (!isValidNavigationState(rawState)) {
    return { listRestore: null }
  }
  const flat = rawState as unknown as MaintenanceListNavigationStateFlat
  return isPlainRecord(flat.filters)
    ? { listRestore: { filters: flat.filters, page: flat.page, sortBy: flat.sortBy, sortOrder: flat.sortOrder } }
    : { listRestore: null }
}

const buildSiblingRequestParams = (filters: MaintenanceFilters) => {
  // Expande tokens sintéticos do filtro de status (issue #244).
  const expanded = expandStatusFilter({
    status: filters.status,
    subcategory: filters.subcategory,
  })
  const params: Record<string, unknown> = {
    page: 1,
    limit: SIBLING_FETCH_LIMIT,
    status: expanded.status,
    urgency: filters.urgency,
    subcategory: expanded.subcategory,
    department: filters.department,
    responsibleId: filters.responsibleId,
    storeIds: filters.storeIds,
    search: filters.search,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    startDate: filters.dateRange?.start ? new Date(filters.dateRange.start).toISOString() : undefined,
    endDate: filters.dateRange?.end ? new Date(filters.dateRange.end).toISOString() : undefined,
  }

  // O backend espera filtros de array como strings separadas por vírgula
  // (mesma normalização do useMaintenance), para a ordem devolvida bater com
  // a ordem visual da listagem.
  for (const key of ['status', 'urgency', 'subcategory', 'department', 'responsibleId', 'storeIds']) {
    if (Array.isArray(params[key])) {
      params[key] = (params[key] as string[]).join(',')
    }
  }
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === '') {
      delete params[key]
    }
  })

  return params as Parameters<typeof apiService.getMaintenanceRequests>[0]
}

// Resolve o conjunto filtrado COMPLETO de IDs (não apenas a página actual).
// Se a chamada falhar, quem invoca degrada para as linhas visíveis.
const fetchAllVisibleIds = (filters: MaintenanceFilters): Promise<string[]> =>
  apiService.getMaintenanceRequests(buildSiblingRequestParams(filters)).then((response) => {
    const list = response.requests || response.items || []
    return list.map((it) => it.id)
  })

export default function MaintenanceList() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showFilters, setShowFilters] = useState(false)
  // elapsedSeconds is now derived in render from storedAt + cacheTick for re-render

  // Adapter de compatibilidade com a PR #213 (factory/issue-206): lê o
  // `location.state` no mount e normaliza tanto o formato nested desta PR
  // ({ listRestore, navigationContext }) quanto o formato flat do #213
  // ({ filters, page, sortBy, sortOrder } no top-level). Permite que o
  // "Voltar" do formulário restaure filtros/paginação em qualquer ordem de
  // merge das duas PRs.
  const { listRestore } = normalizeNavigationState(location.state)

  // Estados para filtros temporários e aplicados. Quando vimos do detalhe
  // (Voltar), restauramos os filtros/ordenação a partir do `listRestore`.
  const [tempFilters, setTempFilters] = useState<MaintenanceFilters>(listRestore?.filters ?? {})
  const [appliedFilters, setAppliedFilters] = useState<MaintenanceFilters>(() =>
    listRestore?.filters
      ? { ...listRestore.filters, sortBy: listRestore.sortBy || 'lastModified', sortOrder: listRestore.sortOrder || 'desc' }
      : { sortBy: 'lastModified', sortOrder: 'desc' }
  )

  // Estado para o dropdown "Responsável" — carrega apenas utilizadores ativos
  const [responsibleUsers, setResponsibleUsers] = useState<
    { id: string; name: string; type: string; store: string; active: boolean }[]
  >([])
  const [responsibleUsersLoading, setResponsibleUsersLoading] = useState(false)
  const [responsibleUsersError, setResponsibleUsersError] = useState<string | null>(null)
  
  // Estados para ordenação
  const [sortBy, setSortBy] = useState(listRestore?.sortBy ?? 'lastModified')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(listRestore?.sortOrder ?? 'desc')
  
  // Estado inicial do hook: quando vimos da tela de detalhe, restauramos
  // filtros/página directamente como estado inicial. Isto evita chamar
  // setFilters()/setPage() no mount — que resetaria a página para 1 e
  // dispararia múltiplos fetches desnecessários. O auto-fetch do mount faz um
  // único request com a view restaurada.
  const initialFilters: MaintenanceFilters = listRestore
    ? { ...(listRestore.filters ?? {}), sortBy, sortOrder }
    : { sortBy, sortOrder }
  const initialPage = listRestore?.page || 1

  // Use o novo hook useMaintenance com cache e debounce
  const {
    items,
    loading,
    error,
    pagination,
    currentPage,
    cacheMetadata,
    refreshInProgress,
    isStale,
    fallbackNotice,
    setFilters: setFiltersHook,
    setPage: setPageHook,
    refresh,
    clearError,
  } = useMaintenance({
    autoFetch: true,
    pageSize: 20,
    cacheTtlSeconds: 60,
    debounceMs: 300,
    initialFilters,
    initialPage,
  })

  // Carregar parceiros para o modal de envio de cotações
  const { partners } = usePartners()

  // Lojas distintas com a contagem de ativos — alimenta os chips de
  // filtro rápido por localização. Substitui o array hardcoded da PR #215.
  const { stores: storesWithUsage, loading: storesLoading } = useStoresWithUsage()

  // Modal states
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showMarkExecutedModal, setShowMarkExecutedModal] = useState(false)
  const [showTriageModal, setShowTriageModal] = useState(false)
  const [showSendQuotationsModal, setShowSendQuotationsModal] = useState(false)
  const [showCFOValueConfirmModal, setShowCFOValueConfirmModal] = useState(false)
  const [showApproveQuotationModal, setShowApproveQuotationModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [currentItemId, setCurrentItemId] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  // Fetch active users for the "Responsável" filter (only authenticated users).
  // Carregado lazily quando o painel de filtros é aberto para evitar custo
  // desnecessário em utilizadores que não usam este filtro.
  const fetchResponsibleUsers = useCallback(async () => {
    try {
      setResponsibleUsersLoading(true)
      setResponsibleUsersError(null)
      const users = await apiService.getActiveUsers()
      // Ordenação alfabética para UX consistente
      const sorted = [...users].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt', { sensitivity: 'base' })
      )
      setResponsibleUsers(sorted)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar responsáveis'
      setResponsibleUsersError(message)
      setResponsibleUsers([])
    } finally {
      setResponsibleUsersLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && showFilters && responsibleUsers.length === 0 && !responsibleUsersLoading) {
      fetchResponsibleUsers()
    }
  }, [isAuthenticated, showFilters, responsibleUsers.length, responsibleUsersLoading, fetchResponsibleUsers])


  // Atualização em tempo real do badge de cache
  // Use a tick counter to force re-render each second; elapsed is derived on render
  const [cacheTick, setCacheTick] = useState(0)
  const storedAt = cacheMetadata.client.storedAt
  // Derived on each render; cacheTick dependency forces re-render every second via interval
  const elapsedSeconds = storedAt && cacheTick >= 0
    ? Math.max(0, Math.floor((Date.now() - storedAt.getTime()) / 1000))
    : 0

  useEffect(() => {
    if (!storedAt) {
      return
    }

    // Configura o intervalo para forçar re-render a cada segundo
    const intervalId = setInterval(() => {
      setCacheTick(t => t + 1)
    }, 1000)

    // Cleanup: limpa o intervalo quando o componente desmonta ou storedAt muda
    return () => {
      if (typeof clearInterval !== 'undefined') {
        clearInterval(intervalId)
      }
    }
  }, [storedAt])

  // A restauração de filtros/página vindos da tela de detalhe é feita de forma
  // atómica via `initialFilters`/`initialPage` do hook (1 fetch no mount). Este
  // effect apenas limpa o `location.state` com `replace` para que um hard-refresh
  // subsequente não reaplique filtros já obsoletos.
  useEffect(() => {
    if (listRestore) {
      navigate(location.pathname, { replace: true, state: null })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounce timer ref para inputs de texto (search). Outras alterações
  // (chips/checkboxes/dates/select) aplicam-se imediatamente para feedback live.
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Helper para construir o objeto de filtros com ordenação, partilhado por
  // handleFilterChange e handleSearch.
  const buildFiltersWithSort = useCallback(
    (filters: MaintenanceFilters): MaintenanceFilters => ({
      ...filters,
      sortBy,
      sortOrder,
    }),
    [sortBy, sortOrder]
  )

  const handleFilterChange = (newFilters: Partial<MaintenanceFilters>) => {
    const updatedFilters = { ...tempFilters, ...newFilters }
    setTempFilters(updatedFilters)
    setAppliedFilters(updatedFilters)

    // Filtragem live (live search / reactive filter): cada seleção num chip
    // ou checkbox aplica imediatamente a lista, sem exigir Enter ou click
    // no botão "Pesquisar". Para o input de texto, usamos um pequeno debounce
    // para não disparar uma request por keystroke (o hook também tem o seu
    // próprio debounce de 300ms; este debounce local evita mutações de state
    // e re-renders desnecessários).
    const isTextSearchOnly =
      newFilters &&
      Object.keys(newFilters).length === 1 &&
      typeof newFilters.search === 'string'

    if (isTextSearchOnly) {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
      }
      searchDebounceTimerRef.current = setTimeout(() => {
        setFiltersHook(buildFiltersWithSort(updatedFilters))
      }, 200)
    } else {
      setFiltersHook(buildFiltersWithSort(updatedFilters))
    }
  }
  const handleSearch = () => {
    console.log('[MaintenanceList] Forçando aplicação imediata dos filtros:', tempFilters)
    // Os filtros são já aplicados live via handleFilterChange. Este botão
    // permanece como atalho para forçar uma re-aplicação imediata (ex.: após
    // um debounce de texto a meio) e para acessibilidade (utilizadores podem
    // preferir confirmar com Enter/click).
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current)
      searchDebounceTimerRef.current = null
    }
    const filtersWithSort = buildFiltersWithSort(tempFilters)
    setAppliedFilters(filtersWithSort)
    setFiltersHook(filtersWithSort)
  }

  const handleClearFilters = () => {
    console.log('[MaintenanceList] Limpando filtros')
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current)
      searchDebounceTimerRef.current = null
    }
    const emptyFilters = {}
    setTempFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setFiltersHook(emptyFilters)
  }

  // Filtro rápido por localização: alterna o valor no array storeIds
  // e aplica imediatamente (integrado ao filtro automático, alinhamento
  // com Tânia Nunes — Call Hendrios 31/07/2026).
  // Lê appliedFilters directamente (fresco a cada clique — React processa
  // eventos discretos de forma síncrona) e chama os setters como siblings.
  // Evita side-effects dentro de updater, que React 18 invoca 2x em
  // StrictMode (duplicaria o request debounced em dev).
  const handleLocationChipToggle = useCallback(
    (storeId: string) => {
      const current = Array.isArray(appliedFilters.storeIds) ? appliedFilters.storeIds : []
      const exists = current.includes(storeId)
      const nextStoreIds = exists
        ? current.filter((s) => s !== storeId)
        : [...current, storeId]

      const filtersWithSort: MaintenanceFilters = {
        ...appliedFilters,
        storeIds: nextStoreIds.length > 0 ? nextStoreIds : undefined,
        sortBy,
        sortOrder
      }
      setAppliedFilters(filtersWithSort)
      // Mantém tempFilters sincronizado para o fluxo "Pesquisar" não descartar os chips
      setTempFilters((prev) => ({
        ...prev,
        storeIds: nextStoreIds.length > 0 ? nextStoreIds : undefined
      }))
      setFiltersHook(filtersWithSort)
    },
    [appliedFilters, setFiltersHook, sortBy, sortOrder]
  )

  // Limpar os chips deriva de appliedFilters (não de tempFilters) para não
  // aplicar acidentalmente filtros stageados que ainda não foram pesquisados.
  const handleLocationChipClear = useCallback(() => {
    const filtersWithSort: MaintenanceFilters = {
      ...appliedFilters,
      storeIds: undefined,
      sortBy,
      sortOrder
    }
    setAppliedFilters(filtersWithSort)
    setTempFilters((prev) => ({ ...prev, storeIds: undefined }))
    setFiltersHook(filtersWithSort)
  }, [setFiltersHook, appliedFilters, sortBy, sortOrder])

  const handleSortChange = (newSortBy: string) => {
    if (newSortBy === sortBy) {
      // Inverter ordem se clicar no mesmo campo
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
      setSortOrder(newSortOrder)

      // Aplicar filtros com nova ordenação
      const filtersWithSort: MaintenanceFilters = {
        ...tempFilters,
        sortBy: newSortBy,
        sortOrder: newSortOrder
      }
      setAppliedFilters(filtersWithSort)
      setFiltersHook(filtersWithSort)
    } else {
      setSortBy(newSortBy)
      setSortOrder('desc') // Padrão: mais recentes primeiro

      // Aplicar filtros com nova ordenação
      const filtersWithSort: MaintenanceFilters = {
        ...tempFilters,
        sortBy: newSortBy,
        sortOrder: 'desc'
      }
      setAppliedFilters(filtersWithSort)
      setFiltersHook(filtersWithSort)
    }
  }

  // Cleanup do debounce timer do input de search quando o componente desmonta
  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
        searchDebounceTimerRef.current = null
      }
    }
  }, [])

  const handleSortOrderToggle = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortOrder(newSortOrder)

    // Aplicar filtros com nova ordenação
    const filtersWithSort: MaintenanceFilters = {
      ...tempFilters,
      sortBy,
      sortOrder: newSortOrder
    }
    setAppliedFilters(filtersWithSort)
    setFiltersHook(filtersWithSort)
  }

  const handlePageChange = (page: number) => {
    setPageHook(page)
  }

  const handleRefresh = () => {
    refresh()
  }

  const formatDate = useCallback((value?: string | Date | null) => {
    if (!value) {
      return '—'
    }
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
      return '—'
    }
    return date.toLocaleDateString('pt-PT')
  }, [])

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR',
      }),
    [],
  )

  const formatCurrency = useCallback(
    (value?: number | null) => {
      if (value === undefined || value === null || isNaN(value)) {
        return '—'
      }
      return currencyFormatter.format(value)
    },
    [currencyFormatter],
  )


  // Formata o "tempo parado" para mostrar horas quando < 24h e dias caso contrário.
  // Mantém compatibilidade com a versão em dias (regra "1 dia" / "N dias") quando
  // o cálculo devolve 0 dias mas existem horas decorridas (ex.: 1h30 -> "1 h").
  const formatDowntime = useCallback((item: MaintenanceItem): string => {
    if (!isMaintenanceRequest(item)) {
      return '—'
    }
    if (item.status !== 'AGUARDANDO_TRIAGEM' && item.status !== 'PENDENTE' && item.status !== 'APROVADO') {
      return '—'
    }

    const now = new Date()
    let startTime: Date

    if (item.approvalDate) {
      startTime = new Date(item.approvalDate)
    } else {
      const updates = Array.isArray(item.updates) ? item.updates : []
      const oldestUpdate = updates.length > 0 ? updates[updates.length - 1] : null
      const oldestUpdateTimestamp = oldestUpdate?.timestamp
        ? new Date(oldestUpdate.timestamp)
        : null
      const candidateDate =
        oldestUpdateTimestamp && !isNaN(oldestUpdateTimestamp.getTime())
          ? oldestUpdateTimestamp
          : item.createdAt
            ? new Date(item.createdAt)
            : item.requestDate
              ? new Date(item.requestDate)
              : null
      startTime = candidateDate && !isNaN(candidateDate.getTime())
        ? candidateDate
        : new Date()
    }

    if (isNaN(startTime.getTime())) {
      return '—'
    }

    const diffMs = now.getTime() - startTime.getTime()
    if (isNaN(diffMs) || diffMs < 0) {
      return '—'
    }

    const totalHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (totalHours < 1) {
      const totalMinutes = Math.max(1, Math.floor(diffMs / (1000 * 60)))
      return `${totalMinutes} min`
    }
    if (totalHours < 24) {
      return `${totalHours} h`
    }
    const diffDays = Math.floor(totalHours / 24)
    return `${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`
  }, [])



  const truncateId = useCallback((id: string) => {
    if (id.length <= 8) return id
    return `${id.substring(0, 4)}...${id.substring(id.length - 4)}`
  }, [])

  // Função para normalizar o texto, deixando a primeira letra de cada palavra em maiúscula
  const normalizeTitle = useCallback((text: string) => {
    if (!text) return text
    
    return text
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Mantém palavras com hífen juntas, mas capitaliza cada parte
        if (word.includes('-')) {
          return word
            .split('-')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join('-')
        }
        // Capitaliza palavras normais
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ')
  }, [])

  const getItemTitle = useCallback((item: MaintenanceItem) => {
    let title = ''

    if (item.type === 'requisicao') {
      title = (
        item.asset?.description ||
        item.equipmentDescription ||
        item.asset?.department ||
        `Ativo ${item.equipmentId}`
      )
    } else {
      title = item.asset?.description || `Ativo ${item.equipmentId}`
    }

    return truncateDescriptionPreview(normalizeTitle(title), 80)
  }, [normalizeTitle])

  const canApprove = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao' || item.status !== 'PENDENTE') {
        return false
      }

      // Compras: a aprovação é feita pelo CFO via cotação, não via este botão
      if (isMaintenanceRequest(item) && item.subcategory === 'compras') {
        return false
      }

      // Admin e CFO sempre podem aprovar (Decision B / §9.1 / D7a)
      if (user.type === 'admin' || user.type === 'cfo') {
        return true
      }

      // Staff e supervisor nunca podem aprovar
      if (user.type !== 'gerente') {
        return false
      }

      // Gerente: usar flag calculada pelo backend
      return item.canManagerApprove === true
    },
    [user],
  )

  const canComplete = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao' || (item.status !== 'APROVADO' && item.status !== 'EXECUTADO')) {
        return false
      }
      // Para compras APROVADO: só exibir se o CFO já aprovou uma cotação
      if (isMaintenanceRequest(item) && item.subcategory === 'compras' && item.status === 'APROVADO') {
        if (!item.selectedQuotationId) {
          return false
        }
        // Gestor, cfo e admin podem registar compra
        return user.type === 'gerente' || user.type === 'cfo' || user.type === 'admin'
      }
      // Only cfo and admin can complete (nova regra)
      return user.type === 'cfo' || user.type === 'admin'
    },
    [user],
  )

  const canTransfer = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao') {
        return false
      }
      // Não permitir transferência em status terminais
      const terminalStatuses = ['CONCLUIDO', 'REJEITADO', 'ARQUIVADO']
      if (terminalStatuses.includes(item.status)) {
        return false
      }
      // Issue #180: staff/supervisor não pode transferir requisições de terceiros
      if ((user.type === 'staff' || user.type === 'supervisor') && (item as MaintenanceRequest).isOwner !== true) {
        return false
      }
      // Gerentes, cfo e admins podem transferir
      return user.type === 'gerente' || user.type === 'cfo' || user.type === 'admin'
    },
    [user],
  )

  const canMarkExecuted = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao' || item.status !== 'APROVADO') {
        return false
      }
      // Para compras, o fluxo é via "Registar Compra" (canComplete), não via este modal
      if (isMaintenanceRequest(item) && item.subcategory === 'compras') {
        return false
      }
      // Issue #180: staff/supervisor não marca como executado em requisições de terceiros
      if ((user.type === 'staff' || user.type === 'supervisor') && (item as MaintenanceRequest).isOwner !== true) {
        return false
      }
      // Supervisor, gerente, cfo e admin podem marcar como executado
      return ['supervisor', 'gerente', 'cfo', 'admin'].includes(user.type)
    },
    [user],
  )

  const canTriage = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao' || item.status !== 'AGUARDANDO_TRIAGEM') {
        return false
      }
      // Issue #180: staff/supervisor não tria requisições de terceiros
      if ((user.type === 'staff' || user.type === 'supervisor') && (item as MaintenanceRequest).isOwner !== true) {
        return false
      }
      // Check backend-calculated permission
      return (item as MaintenanceRequest).canPerformTriage === true
    },
    [user],
  )

  const canSendQuotations = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao') {
        return false
      }
      const req = item as MaintenanceRequest
      // Issue #180: staff/supervisor não envia cotações em requisições de terceiros
      if ((user.type === 'staff' || user.type === 'supervisor') && req.isOwner !== true) {
        return false
      }
      // Must be compras category, emails not sent yet, user has permission, and status PENDENTE or APROVADO
      return req.subcategory === 'compras' &&
             !req.quotationEmailsSentAt &&
             req.canManageQuotations === true &&
             (item.status === 'PENDENTE' || item.status === 'APROVADO')
    },
    [user],
  )

  const canRejectAlways = useCallback(
    (item: MaintenanceItem) => {
      if (!user) {
        return false
      }
      // Gerente, cfo and admin can reject. Staff/supervisor nunca chegam aqui
      // porque já são excluídos pela guarda acima — não há ações de rejeição
      // para eles em nenhuma circunstância.
      if (user.type !== 'gerente' && user.type !== 'cfo' && user.type !== 'admin') {
        return false
      }
      // Cannot reject terminal states
      const terminalStates = ['REJEITADO', 'CONCLUIDO', 'ARQUIVADO']
      return !terminalStates.includes(item.status)
    },
    [user],
  )

  const canCFOConfirm = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.status !== 'EXECUTADO') {
        return false
      }
      // CFO e admin podem confirmar valor final (tanto requisicao quanto registo)
      return user.type === 'cfo' || user.type === 'admin'
    },
    [user],
  )

  const canApproveCFOQuotation = useCallback(
    (item: MaintenanceItem) => {
      if (!user || item.type !== 'requisicao') return false
      const req = item as MaintenanceRequest
      return req.subcategory === 'compras' &&
             req.quotationsFulfilled === true &&
             !req.selectedQuotationId &&
             (user.type === 'cfo' || user.type === 'admin')
    },
    [user],
  )

  // Contagem de cotações pendentes de aprovação CFO
  const awaitingQuotationCount = useMemo(() => {
    if (!user || (user.type !== 'cfo' && user.type !== 'admin')) return 0
    return items.filter(item =>
      isMaintenanceRequest(item) &&
      item.subcategory === 'compras' &&
      !!item.quotationsFulfilled &&
      !item.selectedQuotationId
    ).length
  }, [items, user])

  const handleApprove = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowApprovalModal(true)
  }, [])

  const handleComplete = useCallback(
    (itemId: string) => {
      navigate(`/manutencao/novo?type=registration&requestId=${itemId}`)
    },
    [navigate],
  )

  const handleReject = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowRejectionModal(true)
  }, [])

  const handleTransfer = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowTransferModal(true)
  }, [])

  const handleMarkExecuted = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowMarkExecutedModal(true)
  }, [])

  const handleTriage = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowTriageModal(true)
  }, [])

  const handleSendQuotations = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowSendQuotationsModal(true)
  }, [])

  const handleCFOConfirm = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowCFOValueConfirmModal(true)
  }, [])

  const handleApproveQuotation = useCallback((itemId: string) => {
    setCurrentItemId(itemId)
    setShowApproveQuotationModal(true)
  }, [])

  const handleRequestTransfer = useCallback(async (newResponsibleId: string, comments?: string) => {
    if (!currentItemId) return

    await apiService.assignMaintenanceResponsibility(currentItemId, newResponsibleId, comments)

    toast.success('Responsabilidade transferida com sucesso!', {
      duration: 3000,
      position: 'top-right',
    })

    // Recarregar a lista para mostrar as atualizações
    refresh()
  }, [currentItemId, refresh])

  const handleView = useCallback(
    async (itemId: string, item?: MaintenanceItem) => {
      // Issue #208 — navegação direta por setas entre requisições dentro do
      // filtro ativo. Para que as setas "<" / ">" saibam qual é o conjunto
      // actualmente filtrado, passamos os filtros aplicados E a lista de IDs
      // visíveis via `state` da rota. O MaintenanceForm vai re-aplicar estes
      // filtros para re-popular a lista e permitir navegar sequencialmente.
      const sanitizedFilters: MaintenanceFilters = {
        ...appliedFilters,
        sortBy,
        sortOrder,
        dateRange: appliedFilters.dateRange
          ? {
              start: appliedFilters.dateRange.start
                ? new Date(appliedFilters.dateRange.start)
                : null,
              end: appliedFilters.dateRange.end
                ? new Date(appliedFilters.dateRange.end)
                : null,
            }
          : undefined,
      }

      // As setas prev/next navegam dentro do conjunto filtrado COMPLETO, não
      // apenas dentro da página actual (page boundary, issue #208). Se a
      // re-query do conjunto falhar, degradamos para as linhas visíveis.
      let visibleIds: string[] = []
      let currentIndex = -1
      try {
        visibleIds = await fetchAllVisibleIds(sanitizedFilters)
        currentIndex = visibleIds.indexOf(itemId)
      } catch {
        // IDs ordenados na mesma ordem visual da tabela, para o form poder
        // mostrar prev/next sem precisar de voltar ao backend.
        visibleIds = items.map(i => i.id)
        currentIndex = items.findIndex(i => i.id === itemId)
      }
      if (currentIndex < 0) {
        visibleIds = items.map(i => i.id)
        currentIndex = items.findIndex(i => i.id === itemId)
      }

      const navigationContext: NavigationContextPayload = {
        filters: sanitizedFilters,
        visibleIds,
        currentIndex,
        currentPage,
        pageSize: pagination.limit,
      }

      // Shape partilhado com a PR #213: `listRestore` alimenta o "Voltar" da
      // listagem; `navigationContext` alimenta as setas prev/next. Para
      // coexistir com o formato FLAT do #213 ({ filters, page, sortBy,
      // sortOrder } no top-level), escrevemos AMBOS os formatos — o #213 lê
      // apenas as 4 chaves do topo e ignora o que não conhece; esta PR lê
      // `listRestore`+`navigationContext` e também o flat no mount.
      const navigationState: MaintenanceListNavigationState & MaintenanceListNavigationStateFlat = {
        listRestore: {
          filters: appliedFilters,
          page: currentPage,
          sortBy,
          sortOrder,
        },
        navigationContext,
        // Formato flat (#213): `isValidNavigationState` do #213 valida
        // filters/page/sortBy/sortOrder no top-level.
        filters: appliedFilters,
        page: currentPage,
        sortBy,
        sortOrder,
      }

      if (item && isMaintenanceRequest(item)) {
        const normalizedStatus = `${item.status}`.toUpperCase()

        // Issue #180: ao clicar numa requisição APROVADO/EXECUTADO, entrar
        // no modo "registration" (executar/concluir) APENAS quando o
        // utilizador pode agir sobre ela. Staff/supervisor devem cair no
        // modo read-only (apenas `?type=registration` continua a abrir o
        // formulário, mas o `isReadOnlyMode()` do MaintenanceForm garante
        // que os campos ficam disabled por causa de `isOwner === false`).
        // Mantemos a heurística APROVADO/EXECUTADO → registration para
        // não-donos, deixando o formulário decidir o modo final pela flag
        // `isOwner` que veio do backend.
        if ((normalizedStatus === 'APROVADO' || normalizedStatus === 'EXECUTADO') && item.isOwner === true) {
          navigate(`/manutencao/editar/${itemId}?type=registration&requestId=${itemId}`, {
            state: navigationState,
          })
          return
        }
      }

      navigate(`/manutencao/editar/${itemId}`, {
        state: navigationState,
      })
    },
    [appliedFilters, currentPage, items, navigate, pagination.limit, sortBy, sortOrder],
  )

  const handleRowClick = useCallback(
    (item: MaintenanceItem) => {
      handleView(item.id, item)
    },
    [handleView],
  )

  const columns = useMemo<Column<MaintenanceItem>[]>(() => [
    {
      key: 'summary',
      header: 'Solicitação',
      className: 'relative min-w-0 max-w-[420px] px-0 align-top',
      accessor: item => {
        const StatusIcon = STATUS_ICONS[item.status] || Clock3
        const accentClass = getUrgencyAccentClass(item)
        const tooltipLabel = getUrgencyTooltip(item)

        return (
          <div className="group relative flex items-start gap-4 py-3 pl-6 pr-4">
            <span
              aria-hidden
              className={clsx(
                'absolute left-0 top-2 bottom-2 w-1 rounded-full transition-all duration-200 group-hover:top-1 group-hover:bottom-1 group-hover:w-1.5',
                accentClass,
              )}
            />
            {tooltipLabel ? (
              <>
                <div className="pointer-events-none absolute left-3 top-1/2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-brand-neutral-500 px-2 py-1 text-xs font-medium text-white shadow-lg transition-opacity duration-200 group-hover:block">
                  {tooltipLabel}
                </div>
                <span className="sr-only">{tooltipLabel}</span>
              </>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col gap-2 pl-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="min-w-0 max-w-full truncate text-base font-semibold text-brand-text-primary">{getItemTitle(item)}</p>
                <span className="shrink-0 text-xs font-medium text-brand-text-muted">#{truncateId(item.id)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-brand-text-secondary">
                <span
                  data-testid="status-badge"
                  data-status={item.status}
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
                    STATUS_BADGE_MAP[item.status],
                  )}
                >
                  {StatusIcon && <StatusIcon className="h-3.5 w-3.5" aria-hidden />}
                  {getStatusLabel(item.status, isMaintenanceRequest(item) ? item.subcategory : undefined)}
                </span>
                {isMaintenanceRequest(item) && item.subcategory && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-blue-700 border border-blue-200">
                    {item.subcategory === 'manutencao' ? 'Manutenção' : 'Compras'}
                  </span>
                )}
                {!isMaintenanceRequest(item) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-surface-100 px-2.5 py-1 text-brand-text-secondary">
                    <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                    Registro
                  </span>
                )}
              </div>
              {isMaintenanceRequest(item) && (
                <DescriptionPreview
                  text={item.problemDescription}
                  itemId={item.id}
                />
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'equipmentId',
      header: 'Ativo',
      className: 'whitespace-nowrap px-4 py-3 align-top',
      accessor: item => (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            {item.equipmentId}
          </span>
        </div>
      ),
    },
    {
      key: 'timeline',
      header: 'Linha do tempo',
      className: 'px-4 py-3 align-top min-w-[200px] max-w-sm whitespace-normal',
      accessor: item => {
        const timelineEntries: {
          key: string
          icon: LucideIcon
          label: string
          value: string
          accent?: string
        }[] = isMaintenanceRequest(item)
          ? [
              {
                key: 'requested',
                icon: CalendarDays,
                label: 'Solicitada em',
                value: formatDate(item.requestDate),
              },
              {
                key: 'requester',
                icon: UserCircle,
                label: 'Solicitante',
                value: item.requester?.name ?? item.requesterId ?? '—',
              },
              {
                key: 'responsible',
                icon: ClipboardCheck,
                label: 'Responsável',
                value: item.responsiblePerson?.name ?? item.responsiblePersonId ?? 'Não atribuído',
              },
              item.approvalDate
                ? {
                    key: 'approved',
                    icon: CheckCircle2,
                    label: 'Aprovada em',
                    value: formatDate(item.approvalDate),
                    accent: 'text-brand-primary-600',
                  }
                : null,
              // Adicionar tempo parado na timeline para mobile (apenas para status AGUARDANDO_TRIAGEM, PENDENTE ou APROVADO)
            ].filter(Boolean) as {
              key: string
              icon: LucideIcon
              label: string
              value: string
              accent?: string
            }[]
          : [
              {
                key: 'execution',
                icon: ClipboardCheck,
                label: 'Execução',
                value: item.executedAt ? formatDate(item.executedAt) : '—',
                accent: 'text-brand-accent-600',
              },
              {
                key: 'completedBy',
                icon: UserCircle,
                label: 'Concluída por',
                value: (item as unknown as MaintenanceRequest & { responsibleTechnicianName?: string }).responsibleTechnicianName ?? item.responsibleTechnician ?? '—',
              },
              {
                key: 'completion',
                icon: CalendarDays,
                label: 'Conclusão',
                value: item.completionDate ? formatDate(item.completionDate) : '—',
              },
            ]

        // Calcular última modificação para integrar na timeline
        const candidateDates = [];
        
        if (item.type === 'requisicao') {
          if (item.requestDate) candidateDates.push(new Date(item.requestDate));
          if (item.approvalDate) candidateDates.push(new Date(item.approvalDate));
          if (item.executedAt) candidateDates.push(new Date(item.executedAt));
          const reqItemWithCompletion = item as MaintenanceRequest & { completionDate?: Date | string };
          if (reqItemWithCompletion.completionDate) candidateDates.push(new Date(reqItemWithCompletion.completionDate));
          if (item.scheduledArrangementDate) candidateDates.push(new Date(item.scheduledArrangementDate));

          if (item.updates && Array.isArray(item.updates) && item.updates.length > 0) {
            const latestUpdate = item.updates[0];
            if (latestUpdate.timestamp) candidateDates.push(new Date(latestUpdate.timestamp));
          }
        } else {
          const regItem = item as MaintenanceRegistration;
          if (regItem.requestDate) candidateDates.push(new Date(regItem.requestDate));
          if (regItem.executionDate) candidateDates.push(new Date(regItem.executionDate));
          if (regItem.completionDate) candidateDates.push(new Date(regItem.completionDate));
        }
        
        const validDates = candidateDates.filter(
          date => date instanceof Date && !isNaN(date.getTime())
        );
        
        let lastModified = null;
        if (validDates.length > 0) {
          lastModified = new Date(Math.max(...validDates.map(d => d.getTime())));
        }

        return (
          <div className="flex flex-col gap-3">
            <div className="grid min-w-0 grid-cols-2 gap-x-4 gap-y-3 text-sm text-brand-text-secondary">
              {timelineEntries.map(entry => {
                const IconComponent = entry.icon
                return (
                  <div key={entry.key} className="flex items-center gap-2">
                    <IconComponent
                      className={clsx('h-4 w-4', entry.accent ?? 'text-brand-text-muted')}
                      aria-hidden
                    />
                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide text-brand-text-muted">
                        {entry.label}
                      </span>
                      <span className="font-medium text-brand-text-primary break-words">{entry.value}</span>
                    </div>
                  </div>
                )
              })}
              {/* Adicionar tempo parado e última modificação na mesma linha quando ambos existirem */}
              {isMaintenanceRequest(item) &&
                (item.status === 'AGUARDANDO_TRIAGEM' || item.status === 'PENDENTE' || item.status === 'APROVADO') &&
                formatDowntime(item) !== '—' && (
                  <>
                    <div className="flex items-center gap-2">
                      <Timer className="h-4 w-4 shrink-0 text-brand-warning-600" />
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-brand-text-muted">
                          Tempo parado
                        </span>
                        <span
                          data-testid="downtime-badge"
                          className="font-medium text-brand-text-primary break-words"
                        >
                          {formatDowntime(item)}
                        </span>
                      </div>
                    </div>
                    {/* Integrar indicador de última modificação ao lado do tempo parado */}
                    {lastModified && (
                      <div className="flex items-center gap-2">
                        <LastModifiedIndicator
                          lastModified={lastModified}
                          modifiedBy={item.updates?.[0]?.authorName}
                        />
                      </div>
                    )}
                  </>
                )}
              {/* Caso não haja tempo parado mas tenha última modificação, mostrar em linha separada */}
              {lastModified &&
                (!isMaintenanceRequest(item) ||
                 (item.status !== 'AGUARDANDO_TRIAGEM' && item.status !== 'PENDENTE' && item.status !== 'APROVADO') ||
                 formatDowntime(item) === '—') && (
                  <div className="flex items-center gap-2 col-span-2">
                    <LastModifiedIndicator
                      lastModified={lastModified}
                      modifiedBy={item.updates?.[0]?.authorName}
                    />
                  </div>
                )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'actions',
      header: 'Ações',
      className: 'shrink-0 align-top min-w-[220px] max-w-[280px] whitespace-normal',
      headerClassName: 'text-left sm:text-right',
      accessor: item => (
        <div className="flex flex-col gap-4 sm:items-end">
          <div className="flex flex-col sm:items-end gap-2">
            {/* Custo estimado (apenas para MaintenanceRequest) */}
            {isMaintenanceRequest(item) && (
              <>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text-primary">
                  <CircleDollarSign className="h-4 w-4 text-brand-text-muted" aria-hidden />
                  {formatCurrency(item.estimatedCost)}
                </div>
                <span className="text-xs text-brand-text-muted">Custo estimado</span>
              </>
            )}
            
            {/* Custo real (apenas para MaintenanceRequest com valor definido) */}
            {isMaintenanceRequest(item) && item.actualCost !== undefined && item.actualCost !== null && (
              <>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text-primary">
                  <CircleDollarSign className="h-4 w-4 text-brand-accent-600" aria-hidden />
                  {formatCurrency(item.actualCost)}
                </div>
                <span className="text-xs text-brand-text-muted">Custo real</span>
              </>
            )}
            
            {/* Custo real para MaintenanceRegistration */}
            {!isMaintenanceRequest(item) && (
              <>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text-primary">
                  <CircleDollarSign className="h-4 w-4 text-brand-accent-600" aria-hidden />
                  {formatCurrency(item.actualCost)}
                </div>
                <span className="text-xs text-brand-text-muted">Custo real</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {canTriage(item) && (
              <ActionIconButton
                title="Triar requisição"
                variant="approve"
                icon={FileCheck}
                onClick={() => handleTriage(item.id)}
                testId="btn-triage"
              />
            )}
            {/* HIDE_SEND_QUOTATIONS: mudar `(false as boolean)` para `(true as boolean)` para re-activar */}
            {(false as boolean) && canSendQuotations(item) && (
              <ActionIconButton
                title="Enviar cotações"
                variant="approve"
                icon={Mail}
                onClick={() => handleSendQuotations(item.id)}
                testId="btn-send-quotations"
              />
            )}
            {canApproveCFOQuotation(item) && (
              <ActionIconButton
                title="Aprovar cotação"
                variant="approve"
                icon={CircleDollarSign}
                onClick={() => handleApproveQuotation(item.id)}
                testId="btn-approve-quotation"
              />
            )}
            {canMarkExecuted(item) && (
              <ActionIconButton
                title={isMaintenanceRequest(item) && item.subcategory === 'compras' ? 'Registar compra' : 'Marcar como executado'}
                variant="complete"
                icon={Wrench}
                onClick={() => handleMarkExecuted(item.id)}
                testId="btn-mark-executed"
              />
            )}
            {canCFOConfirm(item) && (
              <ActionIconButton
                title={isMaintenanceRequest(item) && item.subcategory === 'compras' ? 'Concluir compra' : 'Concluir requisição'}
                variant="complete"
                icon={CircleDollarSign}
                onClick={() => handleCFOConfirm(item.id)}
                testId="btn-cfo-confirm"
              />
            )}
            {canApprove(item) && (
              <>
                <ActionIconButton
                  title="Rejeitar solicitação"
                  variant="reject"
                  icon={X}
                  onClick={() => handleReject(item.id)}
                  testId="btn-reject"
                />
                <ActionIconButton
                  title="Aprovar solicitação"
                  variant="approve"
                  icon={Check}
                  onClick={() => handleApprove(item.id)}
                  testId="btn-approve"
                />
              </>
            )}
            {/* Rejeitar sempre disponível para gestor/admin em estados não-terminais */}
            {!canApprove(item) && canRejectAlways(item) && (
              <ActionIconButton
                title="Rejeitar solicitação"
                variant="reject"
                icon={X}
                onClick={() => handleReject(item.id)}
                testId="btn-reject"
              />
            )}
            {canTransfer(item) && (
              <ActionIconButton
                title="Transferir responsabilidade"
                variant="view"
                icon={UserCheck}
                onClick={() => handleTransfer(item.id)}
                testId="btn-transfer"
              />
            )}
            {canComplete(item) && (
              <ActionIconButton
                title={isMaintenanceRequest(item) && item.subcategory === 'compras' ? 'Registar compra' : 'Completar solicitação'}
                variant="complete"
                icon={ClipboardCheck}
                onClick={() => handleComplete(item.id)}
                testId="btn-complete"
              />
            )}
          </div>
        </div>
      ),
    },
  ], [
    canApprove,
    canComplete,
    canTransfer,
    canMarkExecuted,
    canTriage,
    canSendQuotations,
    canApproveCFOQuotation,
    canRejectAlways,
    canCFOConfirm,
    formatCurrency,
    formatDate,
    getItemTitle,
    handleApprove,
    handleComplete,
    handleReject,
    handleMarkExecuted,
    handleTriage,
    handleSendQuotations,
    handleApproveQuotation,
    handleCFOConfirm,
    truncateId,
    formatDowntime,
    handleTransfer,
  ])


  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-brand-surface-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-brand-text-primary">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-brand-text-primary">Solicitações</h1>
          <p className="mt-2 text-brand-text-secondary">
            Gerencie as solicitações do sistema doBeco
          </p>
        </div>

       <div className="bg-white shadow-lg rounded-xl mb-6">
          <div className="px-6 py-6 sm:p-8">
            <div className="space-y-4 mb-6">
              {/* Primeira linha: filtros e ações */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                {/* Ações */}
                <div className="order-1 flex w-full flex-col gap-2 border-t border-brand-neutral-200 pt-3 sm:order-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:border-t-0 sm:border-l sm:border-brand-neutral-200 sm:pt-0 sm:pl-4">
                  {(user?.type === 'staff' || user?.type === 'supervisor' || user?.type === 'gerente' || user?.type === 'cfo' || user?.type === 'admin') && (
                    <button
                      onClick={() => navigate('/manutencao/novo')}
                      data-testid="new-request-btn"
                      className="inline-flex w-full items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50 transition-all duration-200 sm:w-auto"
                    >
                      <svg
                        className="h-4 w-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Nova Requisição
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowExportModal(true)}
                    data-testid="export-open"
                    aria-label="Exportar requisições filtradas"
                    className="inline-flex w-full items-center justify-center rounded-lg border border-brand-neutral-300 bg-white px-3 py-2.5 text-sm font-medium text-brand-text-secondary shadow-sm transition-all duration-200 hover:bg-brand-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:ring-offset-2 sm:w-auto"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Exportar
                  </button>

                  <button
                    onClick={handleRefresh}
                    disabled={refreshInProgress}
                    aria-busy={refreshInProgress}
                    aria-live="polite"
                    aria-label={refreshInProgress ? 'Atualizando dados, aguarde' : 'Atualizar dados'}
                    aria-disabled={refreshInProgress}
                    className={`inline-flex w-full items-center justify-center px-3 sm:px-4 py-2.5 border shadow-sm text-sm font-medium rounded-lg transition-all duration-200 sm:w-auto sm:flex-shrink-0 ${
                      refreshInProgress
                        ? 'border-brand-neutral-300 text-brand-text-muted bg-brand-surface-100 cursor-not-allowed'
                        : 'border-brand-neutral-300 text-brand-text-secondary bg-white hover:bg-brand-surface-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50'
                    }`}
                    title="Atualizar dados"
                  >
                    <svg
                      className={`h-4 w-4 mr-2 ${refreshInProgress ? 'animate-spin' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span className="truncate">{refreshInProgress ? 'Atualizando...' : 'Atualizar'}</span>
                  </button>
                </div>

                {/* Controles de filtros */}
                <div className="order-2 flex w-full flex-col gap-2 sm:order-1 sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`inline-flex w-full items-center justify-center px-3 sm:w-auto sm:justify-start sm:px-4 py-2.5 border shadow-sm text-sm font-medium rounded-lg transition-all duration-200 ${
                      showFilters
                        ? 'border-brand-primary-500 text-brand-primary-600 bg-brand-primary-50 hover:bg-brand-primary-100'
                        : 'border-brand-neutral-300 text-brand-text-secondary bg-white hover:bg-brand-surface-100'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50`}
                  >
                    <svg
                      className={`h-4 w-4 mr-2 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <span className="truncate">{showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}</span>
                  </button>

                  {/* Badge com total de requisições filtradas */}
                  <div
                    className="w-full rounded-full bg-brand-surface-100 px-3 py-1.5 text-center sm:w-auto sm:text-left"
                    data-testid="maintenance-filtered-count-badge"
                  >
                    <span className="text-sm font-medium text-brand-text-secondary">
                      {pagination.total} {pagination.total === 1 ? 'item' : 'itens'}
                    </span>
                  </div>

                  {/* Chips de filtro rápido por localização.
                       Fonte: backend `/api/stores/with-usage` (useStoresWithUsage).
                       Lista derivada dos ativos reais — substitui o array
                       hardcoded `LOCATION_OPTIONS` da PR #215. */}
                  <div
                    className="flex w-full flex-wrap items-center gap-2"
                    data-testid="location-filter-chips"
                    role="group"
                    aria-label="Filtro rápido por localização"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-brand-text-muted">
                      Localização
                    </span>
                    {storesLoading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <span
                          key={i}
                          className="inline-flex h-7 w-24 animate-pulse rounded-full bg-brand-surface-100"
                          aria-hidden
                          data-testid="location-chip-skeleton"
                        />
                      ))
                    ) : storesWithUsage.length === 0 ? null : (
                      storesWithUsage.map((store) => {
                        const isActive =
                          Array.isArray(appliedFilters.storeIds) &&
                          appliedFilters.storeIds.includes(store.value)
                        return (
                          <button
                            key={store.value}
                            type="button"
                            onClick={() => handleLocationChipToggle(store.value)}
                            aria-pressed={isActive}
                            aria-label={`Filtrar por ${store.value}`}
                            data-testid={`location-chip-${slugifyStore(store.value)}`}
                            data-store-value={store.value}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-brand-primary-500 ${
                              isActive
                                ? 'border-brand-primary-500 bg-brand-primary-500 text-white hover:bg-brand-primary-600'
                                : 'border-brand-neutral-300 bg-white text-brand-text-secondary hover:bg-brand-surface-100'
                            }`}
                            title={`${store.value} (${store.assetCount})`}
                          >
                            <span>{store.value}</span>
                            <span
                              className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                                isActive
                                  ? 'bg-white/20 text-white'
                                  : 'bg-brand-surface-100 text-brand-text-muted'
                              }`}
                            >
                              {store.assetCount}
                            </span>
                          </button>
                        )
                      })
                    )}
                    {Array.isArray(appliedFilters.storeIds) && appliedFilters.storeIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleLocationChipClear}
                        aria-label="Limpar filtro de localização"
                        data-testid="location-chip-clear"
                        className="inline-flex items-center rounded-full border border-transparent bg-brand-surface-100 px-2 py-1 text-xs font-medium text-brand-text-secondary hover:bg-brand-surface-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-brand-primary-500"
                      >
                        Limpar
                      </button>
                    )}
                  </div>

                  {/* Chip para CFO: cotações pendentes de aprovação */}
                  {awaitingQuotationCount > 0 && (
                    <button
                      onClick={() => {
                        setFiltersHook({
                          subcategory: ['compras'],
                          awaitingQuotationApproval: true
                        })
                      }}
                      className="w-full sm:w-auto inline-flex items-center gap-1.5 rounded-full bg-brand-primary-50 px-3 py-2 text-sm font-medium text-brand-primary-700 hover:bg-brand-primary-100 transition-colors"
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      Cotações para aprovar ({awaitingQuotationCount})
                    </button>
                  )}
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="border-t border-brand-neutral-200 pt-4 mt-2">
                <div className="bg-brand-surface-100 rounded-xl p-3">
                  {/* Controles de ordenação */}
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-brand-neutral-200">
                    <label className="text-sm font-semibold text-brand-text-primary">
                      Ordenar por:
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={sortBy}
                        onChange={(e) => handleSortChange(e.target.value)}
                        className="rounded-lg border border-brand-neutral-300 bg-white px-3 py-2 text-sm text-brand-text-primary shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-brand-primary-500"
                      >
                        {SORT_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleSortOrderToggle}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-neutral-300 bg-white text-sm text-brand-text-secondary shadow-sm transition-colors duration-200 hover:bg-brand-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-brand-primary-500"
                        title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
                      >
                        {sortOrder === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-2 items-start md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4">
                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Estado
                      </label>
                      <MultiSelectCheckbox
                        options={STATUS_OPTIONS}
                        selected={tempFilters.status || []}
                        onChange={(selectedValues) => handleFilterChange({ status: selectedValues as MaintenanceStatus[] })}
                        placeholder="Selecionar estado..."
                        allLabel="Todos"
                        clearLabel="Limpar"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Urgência
                      </label>
                      <MultiSelectCheckbox
                        options={URGENCY_OPTIONS}
                        selected={tempFilters.urgency || []}
                        onChange={(selectedValues) => handleFilterChange({ urgency: selectedValues })}
                        placeholder="Selecionar níveis de urgência..."
                        allLabel="Todas"
                        clearLabel="Limpar"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Subcategoria
                      </label>
                      <MultiSelectCheckbox
                        options={SUBCATEGORY_OPTIONS}
                        selected={tempFilters.subcategory || []}
                        onChange={(selectedValues) => handleFilterChange({ subcategory: selectedValues as ('manutencao' | 'compras')[] })}
                        placeholder="Selecionar subcategorias..."
                        allLabel="Todas"
                        clearLabel="Limpar"
                      />
                    </div>

                    <div className="space-y-1.5" data-testid="responsible-filter">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Responsável
                      </label>
                      <MultiSelectCheckbox
                        options={responsibleUsers.map((u) => ({
                          label: u.name + (u.type ? ` (${u.type})` : ''),
                          value: u.id
                        }))}
                        selected={tempFilters.responsibleId || []}
                        onChange={(selectedValues) => handleFilterChange({ responsibleId: selectedValues })}
                        placeholder={
                          responsibleUsersLoading
                            ? 'A carregar responsáveis...'
                            : responsibleUsersError
                              ? 'Erro ao carregar responsáveis'
                              : responsibleUsers.length === 0
                                ? 'Nenhum responsável disponível'
                                : 'Selecionar responsáveis...'
                        }
                        allLabel="Todos"
                        clearLabel="Limpar"
                        disabled={responsibleUsersLoading || responsibleUsers.length === 0}
                        testId="responsible-filter-select"
                      />
                      {responsibleUsersError && (
                        <button
                          type="button"
                          onClick={fetchResponsibleUsers}
                          className="text-xs text-brand-primary-600 hover:text-brand-primary-700 focus:outline-none focus:underline"
                        >
                          Tentar novamente
                        </button>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Pesquisar
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          data-testid="search-input"
                          aria-label="Pesquisar por ativo ou descrição"
                          className="block w-full rounded-lg border border-brand-neutral-300 bg-white px-2.5 py-2 pl-8 pr-2.5 text-sm text-brand-text-primary shadow-sm transition-colors duration-200 placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-brand-primary-500 focus:ring-offset-0"
                          placeholder="Ativo ou descrição..."
                          value={tempFilters.search || ''}
                          onChange={(e) => handleFilterChange({ search: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleSearch()
                            }
                          }}
                        />
                        <svg className="absolute left-3 top-3 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Data Inicial
                      </label>
                      <input
                        type="date"
                        className="block w-full rounded-lg border border-brand-neutral-300 bg-white px-2.5 py-2 text-sm text-brand-text-primary shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-brand-primary-500"
                        value={tempFilters.dateRange?.start ? tempFilters.dateRange.start.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : null;
                          handleFilterChange({
                            dateRange: {
                              start: newDate,
                              end: tempFilters.dateRange?.end ?? null
                            }
                          })
                        }}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Data Final
                      </label>
                      <input
                        type="date"
                        className="block w-full rounded-lg border border-brand-neutral-300 bg-white px-2.5 py-2 text-sm text-brand-text-primary shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-brand-primary-500"
                        value={tempFilters.dateRange?.end ? tempFilters.dateRange.end.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : null;
                          handleFilterChange({
                            dateRange: {
                              start: tempFilters.dateRange?.start || null,
                              end: newDate
                            }
                          })
                        }}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center text-sm font-semibold text-brand-text-primary">
                        <svg className="mr-2 h-4 w-4 text-brand-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Ações
                      </label>
                      <div className="flex flex-col gap-1.5">
                        <span
                          className="text-xs text-brand-text-muted"
                          data-testid="filters-live-hint"
                        >
                          Filtros aplicam-se automaticamente
                        </span>
                        <div className="flex flex-col md:flex-row gap-1.5 w-full">
                            <button
                              onClick={handleSearch}
                              data-testid="apply-filters-btn"
                              title="Forçar aplicação imediata dos filtros"
                              className="inline-flex w-full items-center justify-center rounded-lg border border-brand-primary-300 bg-brand-primary-500 px-3 py-2.5 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:ring-offset-2 focus:ring-offset-brand-surface-50"
                            >
                              <Search className="h-3.5 w-3.5 mr-1.5" />
                              Aplicar
                            </button>
                            <button
                              onClick={handleClearFilters}
                              data-testid="clear-filters-btn"
                              className="inline-flex w-full items-center justify-center rounded-lg border border-brand-neutral-300 bg-white px-3 py-2.5 text-xs font-medium text-brand-text-secondary shadow-sm transition-all duration-200 hover:bg-brand-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:ring-offset-2 focus:ring-offset-brand-surface-50"
                            >
                              <svg className="h-3.5 w-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Limpar
                            </button>
                        </div>

                        {/* Badge com número de filtros ativos */}
                        {(appliedFilters.status?.length || appliedFilters.urgency?.length || appliedFilters.subcategory?.length || appliedFilters.responsibleId?.length || appliedFilters.storeIds?.length || appliedFilters.search || appliedFilters.dateRange?.start || appliedFilters.dateRange?.end) && (
                          <div className="rounded-lg border border-brand-primary-200 bg-brand-primary-50 px-3 py-2 text-center">
                            <span className="text-xs font-medium text-brand-primary-700">
                              {[
                                appliedFilters.status?.length && `${appliedFilters.status.length} estados`,
                                appliedFilters.urgency?.length && `${appliedFilters.urgency.length} níveis de urgência`,
                                appliedFilters.subcategory?.length && `${appliedFilters.subcategory.length} subcategorias`,
                                appliedFilters.responsibleId?.length && `${appliedFilters.responsibleId.length} responsáveis`,
                                appliedFilters.storeIds?.length && `${appliedFilters.storeIds.length} localizações`,
                                appliedFilters.search && 'busca',
                                appliedFilters.dateRange?.start && 'data inicial',
                                appliedFilters.dateRange?.end && 'data final'
                              ].filter(Boolean).length} filtro(s) ativo(s)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {fallbackNotice && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-6 rounded-lg border border-brand-warning-200 bg-brand-warning-50 p-4"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-brand-warning-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand-warning-700">Dados preservados em cache</h3>
                <div className="mt-2 text-sm text-brand-warning-700">
                  {fallbackNotice}
                  {cacheMetadata.backend?.preservedDatasetAgeMs && (
                    <span className="block mt-1">
                      Dados preservados há {Math.floor(cacheMetadata.backend.preservedDatasetAgeMs / 1000)} segundos.
                      {cacheMetadata.backend.sheetsErrorCode && (
                        <span className="block text-xs text-brand-warning-600">
                          Código do erro: {cacheMetadata.backend.sheetsErrorCode}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
                <div className="mt-3">
                  <button
                    onClick={clearError}
                    className="text-sm font-medium text-red-600 underline transition hover:text-red-700"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div data-testid="maintenance-list" className="bg-white shadow ring-1 ring-brand-neutral-200 sm:rounded-xl sm:ring-0 sm:border sm:border-brand-neutral-200">
          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-brand-text-secondary">
              <div className="inline-flex items-center gap-2">
                <svg
                  className="h-5 w-5 animate-spin text-brand-primary-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                A carregar...
              </div>
            </div>
          ) : (
            <OptimizedTable<MaintenanceItem>
              data={items}
              loading={loading}
              emptyMessage="Nenhuma requisição de manutenção encontrada"
              onRowClick={handleRowClick}
              columns={columns}
              className="bg-white"
            />
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Seguinte
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    A mostrar{' '}
                    <span className="font-medium">
                      {(currentPage - 1) * (pagination.limit || 20) + 1}
                    </span>{' '}
                    até{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * (pagination.limit || 20), pagination.total || 0)}
                    </span>{' '}
                    de{' '}
                    <span className="font-medium">{pagination.total || 0}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-between items-center">
          {/* Versão do sistema - lado esquerdo */}
          <div className="text-xs text-slate-500/80">
            {getSystemVersion()}
          </div>
          
          {/* Cache atualizado - lado direito */}
          {cacheMetadata.client.storedAt && (
            <div
              role="status"
              aria-live="polite"
              className="text-xs text-slate-500/80"
              aria-label={`${
                isStale ? 'Dados em cache' : 'Cache atualizado'
              } há ${elapsedSeconds} segundos${
                cacheMetadata.client.cacheHitCount > 0
                  ? ` com ${cacheMetadata.client.cacheHitCount} acessos`
                  : ''
              }`}
            >
              {isStale ? 'Dados em cache' : 'Cache atualizado'} há{' '}
              {isNaN(elapsedSeconds) ? 0 : elapsedSeconds}s
              {cacheMetadata.client.cacheHitCount > 0 && (
                <span className="ml-1">
                  ({cacheMetadata.client.cacheHitCount} hits)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Approval Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null
          
          return (
            <ApprovalModal
              isOpen={showApprovalModal}
              onClose={() => {
                setShowApprovalModal(false)
                setCurrentItemId(null)
              }}
              onApprove={async (comments?: string) => {
                if (!currentItemId) return
                
                await apiService.approveMaintenanceRequest(currentItemId, {
                  comments: comments
                })
                
                // Success - refresh list
                refresh()
              }}
              requestId={currentItemId}
              equipmentDescription={isMaintenanceRequest(currentItem) ? currentItem.equipmentDescription : undefined}
              problemDescription={isMaintenanceRequest(currentItem) ? currentItem.problemDescription : undefined}
              estimatedCost={isMaintenanceRequest(currentItem) ? currentItem.estimatedCost : undefined}
              urgency={isMaintenanceRequest(currentItem) ? currentItem.urgency : undefined}
            />
          )
        })()}

        {/* Rejection Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null
          
          return (
            <RejectionModal
              isOpen={showRejectionModal}
              onClose={() => {
                setShowRejectionModal(false)
                setCurrentItemId(null)
              }}
              onReject={async (reason: string) => {
                if (!currentItemId) return
                
                await apiService.rejectMaintenanceRequest(currentItemId, {
                  reason: reason
                })
                
                toast.success('Requisição rejeitada com sucesso!', {
                  duration: 3000,
                  position: 'top-right',
                })
                
                setShowRejectionModal(false)
                setCurrentItemId(null)
                refresh()
              }}
              requestId={currentItemId}
              equipmentDescription={isMaintenanceRequest(currentItem) ? currentItem.equipmentDescription : undefined}
              problemDescription={isMaintenanceRequest(currentItem) ? currentItem.problemDescription : undefined}
              estimatedCost={isMaintenanceRequest(currentItem) ? currentItem.estimatedCost : undefined}
              urgency={isMaintenanceRequest(currentItem) ? currentItem.urgency : undefined}
            />
          )
        })()}
        
        {/* Modal de Transferência de Responsabilidade */}
        {currentItemId && (
          <TransferResponsibilityModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(false)}
            onRequestTransfer={handleRequestTransfer}
            requestId={currentItemId}
            currentResponsibleName={(items.find(item => item.id === currentItemId && item.type === 'requisicao') as MaintenanceRequest | undefined)?.responsiblePerson?.name}
            equipmentDescription={(items.find(item => item.id === currentItemId && item.type === 'requisicao') as MaintenanceRequest | undefined)?.equipmentDescription}
            storeId={(items.find(item => item.id === currentItemId && item.type === 'requisicao') as MaintenanceRequest | undefined)?.storeId}
            departmentId={(items.find(item => item.id === currentItemId && item.type === 'requisicao') as MaintenanceRequest | undefined)?.department}
          />
        )}

        {/* Mark Executed Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null
          
          let scheduledDate: string | undefined = undefined
          if (isMaintenanceRequest(currentItem) && currentItem.scheduledArrangementDate) {
            const date = currentItem.scheduledArrangementDate
            if (typeof date === 'string') {
              scheduledDate = date.split('T')[0]
            } else if (date instanceof Date) {
              scheduledDate = date.toISOString().split('T')[0]
            }
          }
          
          return (
            <MarkExecutedModal
              isOpen={showMarkExecutedModal}
              onClose={() => {
                setShowMarkExecutedModal(false)
                setCurrentItemId(null)
              }}
              onMarkExecuted={async (executedAt: string, notes?: string, attachmentUrls?: string[]) => {
                if (!currentItemId) return

                await apiService.updateMaintenanceStatus(currentItemId, {
                  targetStatus: 'EXECUTADO',
                  executedAt,
                  notes,
                  attachmentUrls
                })
                
                setShowMarkExecutedModal(false)
                setCurrentItemId(null)
                refresh()
              }}
              requestId={currentItemId}
              equipmentDescription={isMaintenanceRequest(currentItem) ? currentItem.equipmentDescription : undefined}
              problemDescription={isMaintenanceRequest(currentItem) ? currentItem.problemDescription : undefined}
              estimatedCost={isMaintenanceRequest(currentItem) ? currentItem.estimatedCost : undefined}
              scheduledArrangementDate={scheduledDate}
            />
          )
        })()}

        {/* Triage Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null

          return (
            <TriageModal
              isOpen={showTriageModal}
              onClose={() => {
                setShowTriageModal(false)
                setCurrentItemId(null)
              }}
              onConfirm={async (decision: 'NADA' | 'RESOLVE' | 'EU_RESOLVO' | 'CONVERTER_EM_COMPRA', notes?: string) => {
                if (!currentItemId) return

                await apiService.performTriage(currentItemId, { decision, notes })

                toast.success('Triagem realizada com sucesso!', {
                  duration: 3000,
                  position: 'top-right',
                })

                setShowTriageModal(false)
                setCurrentItemId(null)
                refresh()
              }}
              request={isMaintenanceRequest(currentItem) ? currentItem : null}
            />
          )
        })()}

        {showExportModal && (
          <ExportReportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            filters={appliedFilters}
          />
        )}

        {/* Send Quotations Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null

          // Filter active partners
          const relevantPartners = partners.filter(p => p.status === 'ativo')

          return (
            <SendQuotationsModal
              isOpen={showSendQuotationsModal}
              onClose={() => {
                setShowSendQuotationsModal(false)
                setCurrentItemId(null)
              }}
              onConfirm={async () => {
                if (!currentItemId) return

                await apiService.sendQuotationEmails(currentItemId)

                toast.success('Pedidos de cotação enviados com sucesso!', {
                  duration: 3000,
                  position: 'top-right',
                })

                setShowSendQuotationsModal(false)
                setCurrentItemId(null)
                refresh()
              }}
              request={isMaintenanceRequest(currentItem) ? currentItem : null}
              partners={relevantPartners}
            />
          )
        })()}

        {/* Approve Quotation Modal (CFO) */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem || !isMaintenanceRequest(currentItem)) return null

          return (
            <ApproveQuotationModal
              isOpen={showApproveQuotationModal}
              onClose={() => {
                setShowApproveQuotationModal(false)
                setCurrentItemId(null)
              }}
              onConfirm={async (quotationId: string, notes?: string) => {
                if (!currentItemId) return

                try {
                  await apiService.approveCFOQuotation(currentItemId, { quotationId, notes })

                  toast.success('Cotação aprovada com sucesso!', {
                    duration: 3000,
                    position: 'top-right',
                  })

                  setShowApproveQuotationModal(false)
                  setCurrentItemId(null)
                  refresh()
                } catch (err) {
                  const message = err instanceof Error ? err.message : 'Erro ao aprovar cotação'
                  toast.error(message, {
                    duration: 5000,
                    position: 'top-right',
                  })
                }
              }}
              quotations={currentItem.quotations || []}
            />
          )
        })()}

        {/* CFO Value Confirm Modal */}
        {currentItemId && (() => {
          const currentItem = items.find(item => item.id === currentItemId)
          if (!currentItem) return null

          return (
            <CFOValueConfirmModal
              isOpen={showCFOValueConfirmModal}
              onClose={() => {
                setShowCFOValueConfirmModal(false)
                setCurrentItemId(null)
              }}
              onConfirm={async (finalValue: number, notes?: string) => {
                if (!currentItemId) return

                try {
                  await apiService.confirmCFOValue(currentItemId, { finalValue, notes })

                  toast.success('Requisição concluída com sucesso!', {
                    duration: 3000,
                    position: 'top-right',
                  })

                  setShowCFOValueConfirmModal(false)
                  setCurrentItemId(null)
                  refresh()
                } catch (err) {
                  const message = err instanceof Error ? err.message : 'Erro ao concluir requisição'
                  toast.error(message, {
                    duration: 5000,
                    position: 'top-right',
                  })
                }
              }}
              request={currentItem as unknown as MaintenanceRequest}
            />
          )
        })()}
      </div>
    </div>
  )
}